import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import { Alert, Appearance, Platform, ScrollView, ViewStyle } from "react-native"
import { Button, FormProps, JalaLoading, Screen, useRouteName } from "../../components"
import { FormikProps, FormikValues } from "formik"
import { Modal, View } from "native-base"
import { color} from "../../theme"
import { goBack } from "../../navigators"
import { observer } from "mobx-react-lite"
import SyncableModel from "../../database/SyncableModel"
import { TxKeyPath } from "../../i18n"
import JalaAnalytic from "../../utils/jala-analytic"

export interface ModelCreateMultipleScreenProps {
  ModelForm: FC<FormProps>,
  modelFormProps?: Omit<FormProps, "onSubmit"> & Record<string, any>,
  modelStore: any,
  Model: typeof SyncableModel,
  colorScheme?: "light" | "dark",
  initialValues?: Record<string, any>
  onSubmit?: (data: any) => void
  onSuccess?: (success: boolean) => boolean
  ignoreDirty?: boolean
  hideOnBlurred?: boolean
  saveTx?: TxKeyPath
  infoComponent?: React.ReactElement | null,

  scrollViewRef?: React.MutableRefObject<ScrollView>
}

const ROOT: ViewStyle = {
  flexGrow: 1
}

const SPACE: ViewStyle = {
  flex: 1,
}

const BUTTON_CONTAINER: ViewStyle = {
  borderTopWidth: 1,
  borderTopColor: color.palette.gray300,
}

const BUTTON: ViewStyle = {
  marginVertical: 8,
  marginHorizontal: 16
}

export const ModelCreateMultipleScreen: FC<ModelCreateMultipleScreenProps> = observer(function ModelCreateMultipleScreen(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    ModelForm, modelFormProps,
    modelStore,
    Model,
    initialValues,
    onSubmit: onSubmitProps,
    onSuccess,
    ignoreDirty,
    hideOnBlurred = true,
    saveTx,
    infoComponent,

    scrollViewRef: scrollViewRefProps,
  } = props;

  const [ isError, setIsError ] = useState<boolean>(true)

  const { routeNameRef, previousRouteNameRef } = useRouteName()

  const scrollViewRef = useRef<ScrollView>(null);

  const { loading, error: modelError } = modelStore || {};

  const modelForm = useRef<FormikProps<FormikValues>>()
  const modelFormRef = useCallback((node) => {
    modelForm.current = node;
    if (modelForm.current) setIsError(!((ignoreDirty || modelForm.current.dirty) && modelForm.current.isValid))
  }, [ignoreDirty])

  useEffect(() => {
    if (!modelStore) return;

    modelStore.init()
  }, [modelStore])

  useEffect(() => {
    if (!modelStore) return;
    if (modelError) {
      if (modelError.response && modelError.response.status === 422) {
        const errors = {}
        Object.keys(modelError.response.data.errors).forEach((key) => {
          errors[key] = (modelError.response.data.errors[key] || [])[0]
        })
        if (modelForm.current) modelForm.current.setErrors(errors)
        modelStore.set('error', null)
      }
      else {
        if (Platform.OS === 'web') {
          if (modelError.response)
            confirm(modelError.response.originalError.message)
          modelStore.set('error', null)
        }
        else
          Alert.alert(
            'Error',
            modelError.message,
            [ { onPress: () => modelStore.set('error', null) } ]
          );
      }
    }
  }, [modelError, modelStore])

  const onSubmit = useCallback(async (data) => {
    if (!modelStore) return;
    modelStore.set('loading', true)
    let success = true;
    const keyName = new Model().getKeyName();
    const models = await Model.fetch({ [`${keyName}__in`]: data.models.map((m) => m[keyName]).join(',') })
    for (let i = 0; i < data.models.length; i++) {
      const key = data.models[i][keyName];
      const model = key && models.find((m) => m[keyName] === key)
      if (success) {
        if (!model) success = await modelStore.store(data.models[i])
        else if (model && data.models[i]._action === 'delete') success = await modelStore.delete(model)
        else success = await modelStore.update(model, data.models[i])
      }
    }

    JalaAnalytic.log("Mobile app: Form On Submit", {
      form: ModelForm.displayName || 'Unknown',
      data: JSON.stringify(data || null),
      success,
      routeName: routeNameRef.current,
      previousRouteName: previousRouteNameRef.current
    })

    if ((!onSuccess || onSuccess(success)) && success) {
        goBack('Home.Stack')
    }
    modelStore.set('loading', false)
  }, [Model, ModelForm.displayName, modelStore, onSuccess, previousRouteNameRef, routeNameRef])

  const handleSubmit = useCallback((event) => {
    if (modelForm.current) modelForm.current.handleSubmit(event)
  }, [])

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={hideOnBlurred}
      scrollViewRef={(instance) => {
        scrollViewRef.current = instance;
        if (scrollViewRefProps) scrollViewRefProps.current = instance
      }}
    >
      <ModelForm
        initialValues={initialValues}
        colorScheme={colorScheme}
        innerRef={modelFormRef}
        onSubmit={onSubmitProps || onSubmit}
        loading={loading}
        parentScrollViewRef={scrollViewRef}
        {...modelFormProps}
      />
      <View style={SPACE} />
      <View style={BUTTON_CONTAINER}>
        {infoComponent}
        <Button
          testID="jala:id/save-button"
          tx={saveTx || "save"}
          colorScheme={colorScheme}
          disabled={isError}
          loading={loading}
          onPress={handleSubmit}
          style={BUTTON}
        />
      </View>
      <Modal isOpen={loading}>
        <View
          width="72px" height="72px"
          alignItems="center"
          justifyContent="center"
          rounded="48px"
          backgroundColor="rgba(255, 255, 255, 0.75)"
        >
          <JalaLoading />
        </View>
      </Modal>
    </Screen>
  )
})
